import { apiClient, ApiError } from '$lib/api/client';

/**
 * Exporting, for a signed-in customer.
 *
 * The subscription buys access to the app, not a copy of the data. Browsing is
 * unlimited within fair use and costs nothing beyond the per-seat fee; an
 * export is a copy leaving the app and is charged at the same tiers a one-time
 * purchase pays, less whatever discount was agreed.
 *
 * This is not the anonymous order route with a token on it. That one collects
 * a name and address because there is nowhere else to get them, sits behind
 * Turnstile because there is no better guard, and always takes the money
 * first — none of which is right for a customer we already know, have an
 * address for, and may have agreed to invoice.
 *
 * See docs/refactor/exports.md.
 */

/** One line of what an export comes to. */
export interface PriceLine {
  description: string;
  quantity: number;
  price: number;
  total: number;
}

/** What an export would cost. */
export interface ExportQuote {
  /**
   * What is actually being bought, which is not the number of identifiers
   * sent: a company with no deliverable contact is not a row.
   */
  rows: number;

  lines: PriceLine[];
  net: number;
  vat: number;
  gross: number;

  /** Already reflected in the figures, and shown as a line of its own. */
  discount_percent: number;

  /** "prepaid" or "postpaid". */
  billing_mode: 'prepaid' | 'postpaid';

  /** Stated rather than assumed; a client that guessed would guess wrong. */
  currency: string;
}

/** A placed export. */
export interface PlacedExport extends ExportQuote {
  order_id: number;

  /**
   * Whether the data is on its way without further payment.
   *
   * True for postpaid. False means the invoice and the payment link are on
   * their way instead, and the export follows once it is paid.
   */
  delivered: boolean;
}

/** What to export. */
export interface ExportRequest {
  company_ids: string[];
  contact_ids?: string[];
}

/** An export the server refused, with the code it refused under. */
export class ExportRefused extends Error {
  constructor(
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'ExportRefused';
  }
}

/**
 * Ask what an export would cost.
 *
 * Creates nothing. The app needs the figure before it asks anybody to confirm,
 * and asking for a price should not be an act with a consequence.
 */
export async function quoteExport(wanted: ExportRequest): Promise<ExportQuote> {
  try {
    const { data } = await apiClient.post<ExportQuote>('/orders/quote', wanted);
    return data;
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Place the export.
 *
 * **Calling this is the confirmation.** There is no `confirm: true` flag,
 * because a flag would make the dangerous call and the safe one the same call
 * distinguished by a field somebody can forget.
 */
export async function placeExport(wanted: ExportRequest): Promise<PlacedExport> {
  try {
    const { data } = await apiClient.post<PlacedExport>('/orders', wanted);
    return data;
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Turn a refusal into something with a code on it.
 *
 * `NO_BILLING_ADDRESS` is the one worth keeping: a postpaid account with no
 * invoice address is refused rather than delivered, because the alternative is
 * data gone and an invoice that cannot be addressed. It is fixed by us, not by
 * the customer, so the message says to get in touch rather than offering a form.
 */
function refusal(error: unknown): ExportRefused {
  if (error instanceof ApiError) {
    const body = error.data as { code?: string; error?: string } | undefined;
    if (body?.code) {
      return new ExportRefused(body.code, body.error ?? 'Der Export wurde abgelehnt.');
    }
  }
  return new ExportRefused('EXPORT_FAILED', 'Der Export konnte nicht ausgeführt werden.');
}

/** One past export, as the history shows it. */
export interface PastExport {
  order_id: number;
  placed_at: string;
  rows: number;

  net: number;
  vat: number;
  gross: number;
  currency: string;

  /**
   * Whether an amount was recorded.
   *
   * False for orders placed before the figures were stored on the order. Those
   * are not free ones, and the screen says so rather than printing CHF 0.00.
   */
  priced: boolean;

  discount_percent: number;
  billing_mode: 'prepaid' | 'postpaid';

  payment_status: string;

  /** Whether the export does not wait on money — paid, or postpaid. */
  settled: boolean;

  invoice_number?: string;

  /** The Stripe link, while an unpaid prepaid order is waiting for it. */
  payment_url?: string;

  /** When the data went out, absent until it has. */
  delivered_at?: string;
}

/**
 * What this organisation has exported, newest first.
 *
 * The organisation's rather than the caller's own: a seat shares the
 * subscription, the invoice address and the terms, so a colleague's export is
 * shared spending and belongs in everybody's view of it.
 */
export async function fetchExportHistory(): Promise<PastExport[]> {
  try {
    const { data } = await apiClient.get<{ orders: PastExport[] }>('/orders');
    return data.orders ?? [];
  } catch (error) {
    throw refusal(error);
  }
}

/**
 * Download a past export, reassembled from the corpus as it stands now.
 *
 * Free and repeatable: the export was paid for when it was placed, and a
 * subscription is what keeps the list worth coming back to. Every download is
 * the current state of the rows — a new telephone number, a company that has
 * moved — with two columns saying which rows have gone stale.
 *
 * Fetched rather than opened as a link because the route needs the
 * Authorization header, which a plain `<a href>` cannot carry.
 */
export async function downloadExport(orderID: number, filename: string): Promise<void> {
  // Text rather than a blob: ApiClient parses anything that is not JSON as
  // text, which is what a CSV is. Asking it for a blob would mean teaching the
  // client a response type for one caller.
  const { data } = await apiClient.get<string>(`/orders/${orderID}/download`);

  // A BOM, because the file is opened in Excel more often than anywhere else
  // and Excel reads UTF-8 without one as Latin-1 — which turns every Müller
  // into MÃ¼ller.
  const url = URL.createObjectURL(new Blob(['\ufeff' + data], { type: 'text/csv;charset=utf-8' }));
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
  } finally {
    // Released on the next tick rather than immediately: revoking before the
    // click has been handled cancels the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
